import React, { useCallback, useEffect, useRef } from "react";
import {
    Pagination,
    Box,
    Header,
    Table,
    Button,
    SpaceBetween,
    Spinner
} from "@amzn/awsui-components-react/polaris";
import { getExcaliburAuditData, fetchTransactionTaskPage, downloadTaskFile as downloadTaskFileByTaskId } from "../../excalibur/components/excaliburAPIs";
import { formatDateWithTime } from "../../../common/utils/apiUtils";
import { statusColorCodes } from '../../../common/utils/commonUtils';
import EllipsisText from "../../../common/ellipsisText/EllipsisText";
import {downloadFolder, uploadFiles} from "../../../common/utils/s3Utils";
import {
    ARTIFACTS_FORMAT,
    REMOTE_FILING_TRANSACTION_TYPE,
    COLOR_RED,
    EXCALIBUR_AUDIT_INFO,
    TRANSACTION_DETAIL_INFO
} from "../../../common/constants/constants";
import AppConfig from "../../../AppConfig";
import {getFilingReceipt} from "../utils/filingAPIs";
import {generateErrorDetailsMessage, getFileNameFromS3Key, hasSubmissionReceipt} from "../utils/Utils";
import {
    createTaskPageLoader,
    failedStatusesForRoute,
    getLoadedPageCount,
    getTaskPage,
    rowsNeededThrough
} from "../utils/taskPager";
import Link from "@amzn/awsui-components-react/polaris/link";
import CopyToClipboard from "@amzn/awsui-components-react/polaris/copy-to-clipboard";
import { ROUTE_PATH_TRANSACTION_DETAILS_PAGE } from "../../../common/constants/route_paths";
import { useNavigate, useLocation } from "react-router-dom";
import { getTransactionDetailColumnDefinitions, transactionDetailVisibleColumns, isTransformationRoute, taskDownloadKey } from "./filingTableConfig";

const Actiontable = (params) => {

    const { table, tableDispatcher, tableHeader, visibleColumns, transactionId, clientId, tableContext } = params;
    const [loading, setLoading] = React.useState(false);
    // Names the single download cell whose request is in flight, so only that cell is disabled
    // rather than every download button on the page.
    const [downloadingTaskKey, setDownloadingTaskKey] = React.useState(null);
    // Transaction Details pages its task rows itself (failures first, fetched a few pages ahead),
    // outside the shared reducer. taskRows is the buffer loaded so far; tasksDone means the
    // backend has nothing left; tasksCapped means the loader hit its fetch cap with rows still
    // unread; taskLoadError means the last fetch failed. Either of the last two closes the pager at
    // the loaded pages. A failed load is reported in exactly one place: the empty state while no
    // rows are on screen, otherwise the table header; both offer Retry.
    const taskLoaderRef = useRef(null);
    const [taskRows, setTaskRows] = React.useState([]);
    const [tasksDone, setTasksDone] = React.useState(false);
    const [tasksCapped, setTasksCapped] = React.useState(false);
    const [taskLoadError, setTaskLoadError] = React.useState(false);
    const [taskPageIndex, setTaskPageIndex] = React.useState(1);
    const [taskRefreshToken, setTaskRefreshToken] = React.useState(0);

    const navigate = useNavigate();
    const location = useLocation();

    const downloadArtifacts = async (inputFileKey, s3Bucket) => {
        setLoading(true);
        try {
            await downloadFolder(inputFileKey, s3Bucket, params.domain)
        } catch (error) {
            tableDispatcher({
                type: "SET_FLASH_BAR", payload: {
                    msg: 'Error occured while downloading the file',
                    type: 'error',
                    dispatcher: tableDispatcher
                }
            })
        }
        setLoading(false);
    }

    // Per-task download for the Transaction Details page. Unlike downloadArtifacts (which reads
    // S3 directly with the browser's credentials), this asks TwinService for an Excalibur-signed
    // URL scoped to one taskId, so the website needs no S3 permission of its own.
    const downloadTaskFile = async (taskId, fileType) => {
        setLoading(true);
        setDownloadingTaskKey(taskDownloadKey(taskId, fileType));
        try {
            await downloadTaskFileByTaskId({
                transactionId,
                taskId,
                fileType,
                clientId
            });
        } catch (error) {
            tableDispatcher({
                type: "SET_FLASH_BAR", payload: {
                    msg: 'Error occured while downloading the file',
                    type: 'error',
                    dispatcher: tableDispatcher
                }
            })
        } finally {
            // Reset in finally so a throw anywhere above -- including from the error dispatch
            // itself -- cannot leave the spinner up and the cell permanently unclickable.
            setLoading(false);
            setDownloadingTaskKey(null);
        }
    }

    const downloadSubmissionReceipt = (audit) => {
        if(audit.generatedFileKey){
            downloadArtifacts(audit.generatedFileKey, AppConfig[params.domain].FILING_RECEIPT_BUCKET);
        } else {
            getFilingReceipt(tableDispatcher, audit.useCase, audit.subUseCase, audit.transactionId, params.domain, setLoading);
        }
    }

    const uploadArtifacts = async (audit, files) => {
        setLoading(true);
        try {
            await uploadFiles(files, AppConfig[params.domain].ARTIFACTS_BUCKET, audit.inputFileKey, params.domain)
        } catch (error) {
            tableDispatcher({
                type: "SET_FLASH_BAR", payload: {
                    msg: 'Error occured while uploading the file',
                    type: 'error',
                    dispatcher: tableDispatcher
                }
            })
        }
        setLoading(false);
    }

    const navigateToTransactionDetailsPage = (transaction) => {
        navigate(ROUTE_PATH_TRANSACTION_DETAILS_PAGE, {
            state: {
                data: transaction
            }
        });
    }

    const renderErrorDetails = (record) => {
        if (record.status === 'FAILURE') {
            return (
                <SpaceBetween direction="horizontal" size="xs">
                    <div style={{ color: COLOR_RED }}>{generateErrorDetailsMessage(record.additionalDataMap)}</div>
                    <Link onClick={() => navigateToTransactionDetailsPage(record)}>More Info</Link>
                </SpaceBetween>
            );
        }
        return null;
    };

    useEffect(() => {
        const fetchData = async () => {
            if (table.initialize && tableContext === EXCALIBUR_AUDIT_INFO) {
                await getExcaliburAuditData(tableDispatcher, table.lastEvaluatedKey, REMOTE_FILING_TRANSACTION_TYPE);
            }
        };

        fetchData();
    }, [table.initialize, table.lastEvaluatedKey, transactionId, clientId, tableContext, tableDispatcher]);

    // Asks the current loader for enough rows to show `pageIndex` with the lookahead pages behind it.
    // Results from a loader that has since been replaced (refresh, other transaction) are dropped.
    const loadTasksThrough = useCallback(pageIndex => {
        const loader = taskLoaderRef.current;
        if (!loader) {
            return;
        }
        loader.loadUpTo(rowsNeededThrough(pageIndex))
            .then(({ rows, done, capped, fetched }) => {
                if (taskLoaderRef.current !== loader) {
                    return;
                }
                setTaskRows(rows);
                setTasksDone(done);
                setTasksCapped(capped);
                // Only a fetch that actually ran can clear a failed one. Stepping back onto a page
                // already in the buffer fetches nothing, so the error note and the closed pager
                // stay until the missing rows are really loaded.
                if (fetched > 0) {
                    setTaskLoadError(false);
                }
                if (done || capped) {
                    // The next arrow is live while the end is unknown, so the user may have stepped
                    // past the last page that exists (or will ever be loaded); pull them back onto it.
                    setTaskPageIndex(index => Math.min(index, getLoadedPageCount(rows)));
                }
            })
            .catch(() => {
                if (taskLoaderRef.current !== loader) {
                    return;
                }
                setTaskLoadError(true);
                // The pager closes at the loaded pages while the error stands, so step back onto them.
                setTaskPageIndex(index => Math.min(index, getLoadedPageCount(loader.snapshot().rows)));
            });
    }, []);

    // Re-attempts the fetch that failed, from the same cursor, keeping what is already loaded.
    const retryTasks = () => loadTasksThrough(taskPageIndex);

    // The route decides what kind of transaction this is: /excalibur/... is a transformation,
    // /filing/... a filing. It picks the second download column's meaning and which failed
    // statuses round 1 fetches -- see isTransformationRoute and failedStatusesForRoute.
    const isTransformation = isTransformationRoute(location?.pathname);

    // New loader per transaction (and per refresh click), starting from page 1.
    useEffect(() => {
        if (tableContext !== TRANSACTION_DETAIL_INFO || !transactionId) {
            return undefined;
        }
        const loader = createTaskPageLoader(
            (status, startKey) => fetchTransactionTaskPage(transactionId, status, startKey),
            { failedStatuses: failedStatusesForRoute(isTransformation) }
        );
        taskLoaderRef.current = loader;
        setTaskRows([]);
        setTasksDone(false);
        setTasksCapped(false);
        setTaskLoadError(false);
        setTaskPageIndex(1);
        loadTasksThrough(1);
        return () => {
            if (taskLoaderRef.current === loader) {
                taskLoaderRef.current = null;
            }
        };
    }, [tableContext, transactionId, taskRefreshToken, loadTasksThrough, isTransformation]);

    const onTaskPageChange = pageIndex => {
        setTaskPageIndex(pageIndex);
        loadTasksThrough(pageIndex);
    };

    // Dedicated per-task column set for the Transaction Details page.
    const transactionDetailColumnDefinitions = getTransactionDetailColumnDefinitions({
        downloadTaskFile,
        isTransformation,
        downloadingTaskKey
    });

    const isTransactionDetail = tableContext === TRANSACTION_DETAIL_INFO;
    const currentPageItems = table.auditData.get(table.currentPageIndex);
    // Task table: slice the loaded buffer. A transaction with no task rows shows the empty state;
    // the parent record is not substituted, its input file is not a task's input.
    const currentTaskPage = getTaskPage(taskRows, taskPageIndex);
    const taskItems = currentTaskPage;
    // Spinner only while the page being viewed has nothing to show yet; a page that is still
    // topping up shows what it has.
    const tasksLoading = !tasksDone && !tasksCapped && !taskLoadError && currentTaskPage.length === 0;
    // A failed load with nothing on screen must not look like an empty transaction.
    const showTaskLoadError = isTransactionDetail && taskLoadError && currentTaskPage.length === 0;
    // While a load has failed or the cap was hit, only the loaded pages are offered: no open-ended
    // next arrow that would re-run the failing fetch or imply more pages are coming.
    const taskPagerOpenEnd = isTransactionDetail && !tasksDone && !tasksCapped && !taskLoadError;
    // Header note while rows are on screen; with nothing loaded the empty state carries the error.
    const showTaskErrorInHeader = isTransactionDetail && taskLoadError && !showTaskLoadError;
    let taskHeaderNote;
    if (showTaskErrorInHeader) {
        taskHeaderNote = "Not all tasks could be loaded.";
    } else if (isTransactionDetail && tasksCapped) {
        taskHeaderNote = `Showing the first ${taskRows.length} tasks only. This transaction is too large to load completely.`;
    }

    const tableItems = isTransactionDetail ? taskItems : currentPageItems;

    const emptyState = showTaskLoadError
        ? (
            <Box textAlign="center" color="inherit">
                <b>Could not load tasks</b>
                <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                    The transaction tasks could not be loaded.
                </Box>
                <Button onClick={retryTasks}>Retry</Button>
            </Box>
        )
        : (
            <Box textAlign="center" color="inherit">
                <b>No files</b>
                <Box
                    padding={{ bottom: "s" }}
                    variant="p"
                    color="inherit">
                    No files to display.
                </Box>

            </Box>
        );

    return (
        <>
            {loading ? <Spinner className={"appLoader"} size="large" variant={"disabled"}>Loading</Spinner> : null}
        <Table
            loading={isTransactionDetail ? tasksLoading : table.loading}
            loadingText={isTransactionDetail ? "Loading tasks" : table.loadingText}
            onSortingChange={((event)=>{
                tableDispatcher({type:"AUDIT_SORTING_CHANGED",
                    payload:{isDescending:event.detail.isDescending,
                        sortingField:event.detail.sortingColumn.sortingField}});
            })}
            sortingColumn={table.sortingColumn}
            sortingDescending={table.sortingDescending}
            resizableColumns
            columnDefinitions={isTransactionDetail ? transactionDetailColumnDefinitions : [

                {
                    id: "useCase",
                    header: "Country",
                    cell: record => record.useCase,
                    sortingField: "useCase",
                    width: 120,
                    minWidth: 120
                },
                {
                    id: "subUseCase",
                    header: "Filing Type",
                    cell: record => record.subUseCase,
                    sortingField: "subUseCase",
                    width: 180,
                    minWidth: 180
                },
                {
                    id: "transactionId",
                    header: "Transaction Id",
                    cell: record => (
                        <div>
                            <Link onClick={() => navigateToTransactionDetailsPage(record)}>
                                {record.transactionId}
                            </Link>
                            <CopyToClipboard
                                copyErrorText="Failed to copy!"
                                copySuccessText="Attribute Copied"
                                textToCopy={record.transactionId}
                                variant="icon"
                            />
                        </div>
                    ),
                    sortingField: "transactionId",
                    width: 360,
                    minWidth: 360
                },
                {
                    id: "taxIdentifier",
                    header: "Tax Identifier",
                    cell: record => record.additionalDataMap?.VRN,
                    width: 150,
                    minWidth: 150
                },
                {
                    id: "status",
                    header: "Status",
                    cell: record => {
                        return (<div style={statusColorCodes(record.status)}>{record.status}</div>)
                    },
                    width: 200,
                    minWidth: 200
                },
                {
                    id: "startTimestamp",
                    header: "Request Created on",
                    cell: record => formatDateWithTime(record.startTimestamp),
                    sortingField: "startTimestamp",
                    width: 190,
                    minWidth: 190
                },
                {
                    id: "createdBy",
                    header: "Submitted By",
                    cell: record => record.createdBy,
                    width: 130,
                    minWidth: 130
                },
                {
                    id: "inputFileKey",
                    header: "Input File",
                    cell: record => {
                        return (
                                <Button iconName="download" variant={"link"} className="ellipsisTextButton"
                                    onClick={() => downloadArtifacts(record.inputFileKey, record.inputFileBucket)}>
                                    <EllipsisText text={getFileNameFromS3Key(record.inputFileKey)} fallback="File" fill />
                                </Button>
                        )
                    },
                    width: 260,
                    minWidth: 260
                },
                {
                    id: "submissionDetails",
                    header: "Submission Details",
                    cell: record => JSON.stringify(record.additionalDataMap),
                },
                {
                    id: "getReceipt",
                    header: "Submission Receipt",
                    cell: record => {
                        return (
                            <Button iconName="download" variant={"link"} className="ellipsisTextButton"
                                    disabled={!hasSubmissionReceipt(record)}
                                    onClick={() => downloadSubmissionReceipt(record)}>
                                <EllipsisText text={getFileNameFromS3Key(record.generatedFileKey)} fallback="Receipt" fill />
                            </Button>
                        )
                    },
                    width: 260,
                    minWidth: 260
                },
                {
                    id: "errorDetails",
                    header: "Error Details",
                    cell: record => record.errorDetails,
                    width: 400,
                    minWidth: 400
                },
                {
                    id: "transactionDetails",
                    header: "Error Details",
                    cell: record => renderErrorDetails(record),
                    width: 400,
                    minWidth: 400
                },
                {
                    id: "txnArtifacts",
                    header: "Artifacts",
                    cell: record => {
                        return (
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button iconName="download"  onClick={() =>
                                    downloadArtifacts(record.inputFileKey, AppConfig[params.domain].ARTIFACTS_BUCKET)}>View</Button>
                                <Button iconName="upload" variant="primary">
                                    <input className="inputButton hidden" type={"file"} accept={ARTIFACTS_FORMAT} name="file" onChange={(event) => uploadArtifacts(record, event.target.files)} multiple/>
                                    Upload
                                </Button>
                            </SpaceBetween>
                        )
                    },
                    minWidth: 300
                }
            ]}
            visibleColumns={isTransactionDetail ? transactionDetailVisibleColumns : visibleColumns}
            empty={emptyState}
            filter={
                <Header
                    variant="h2"
                    description={taskHeaderNote}
                    actions={showTaskErrorInHeader
                        ? <Button onClick={retryTasks}>Retry</Button>
                        : undefined}>
                    {tableHeader}
                </Header>
            }
            items={tableItems}
            pagination={
                <Pagination
                    currentPageIndex={isTransactionDetail ? taskPageIndex : table.currentPageIndex}
                    pagesCount={isTransactionDetail ? getLoadedPageCount(taskRows) : table.pageCount}
                    // Dots after the last known page while the backend still has rows to give.
                    openEnd={taskPagerOpenEnd}
                    onChange={event => {
                        if (isTransactionDetail) {
                            onTaskPageChange(event.detail.currentPageIndex);
                            return;
                        }
                        tableDispatcher({type:"PAGE_INDEX_CHANGED",
                            payload:{pageIndex:event.detail.currentPageIndex, dispatcher:tableDispatcher,
                                tableContext:tableContext, transactionId:transactionId}})
                    }
                    }
                    ariaLabels={{
                        nextPageLabel: "Next page",
                        previousPageLabel: "Previous page",
                        pageLabel: pageNumber =>
                            `Page ${pageNumber} of all pages`
                    }}
                />
            }
            preferences={
                <Button iconName="refresh" variant="icon"
                        onClick={()=>{
                            if (isTransactionDetail) {
                                setTaskRefreshToken(token => token + 1);
                                return;
                            }
                            tableDispatcher({type:"REFRESH_AUDIT",payload:{dispatcher:tableDispatcher,
                                tableContext:tableContext, transactionId:transactionId}})
                        }}
                />
            }
        />
        </>
    );
}

export default Actiontable;